import { useState, useEffect } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#how-it-works", label: "How It Works" },
    { href: "#passengers", label: "Passengers" },
    { href: "#drivers", label: "Drivers" },
    // { href: "#safety", label: "Safety" },
    // { href: "#pilot-route", label: "Pilot Route" },
  ];

  return (
    <div className="fixed top-4 left-0 right-0 z-50 flex justify-center px-4">
      {/* Pill navbar */}
      <nav
        className={cn(
          "w-full max-w-4xl transition-all duration-500",
          isScrolled
            ? "bg-white/90 backdrop-blur-xl shadow-xl shadow-black/10 border border-gray-200/80"
            : "bg-white/70 backdrop-blur-md border border-white/60 shadow-lg shadow-black/5",
          "rounded-full",
        )}
      >
        <div className="flex items-center justify-between h-14 px-4 lg:px-5">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 group flex-shrink-0">
            <img
              src="/yenko.png"
              alt="Yenko Logo"
              width={90}
              height={90}
              className="transition-transform group-hover:scale-105"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100/80 rounded-full transition-all"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <a
              href="#contact"
              className="px-3 py-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100/80 transition-all"
            >
              Contact
            </a>
            <a
              href="#waitlist"
              className="flex items-center gap-1.5 bg-[#0057FF] hover:bg-[#0045CC] text-white rounded-full px-4 py-1.5 text-sm font-bold shadow-sm transition-all group"
            >
              Join waitlist
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={cn(
              "lg:hidden p-2 rounded-full transition-all",
              isOpen
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-900 hover:bg-gray-200",
            )}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile dropdown — sits just below the pill */}
      {isOpen && (
        <div className="absolute top-[4.5rem] left-4 right-4 lg:hidden">
          <div className="bg-white/95 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-2xl shadow-black/10 px-4 py-4 space-y-1 animate-slide-down">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-2.5 px-4 text-sm font-bold text-gray-700 hover:text-[#0057FF] hover:bg-gray-50 rounded-2xl transition-all"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              className="block py-2.5 px-4 text-sm font-bold text-gray-700 hover:text-[#0057FF] hover:bg-gray-50 rounded-2xl transition-all"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </a>
            <div className="pt-2 border-t border-gray-100">
              <a
                href="#waitlist"
                className="flex items-center justify-center gap-2 w-full py-3 bg-[#0057FF] hover:bg-[#0045CC] text-white rounded-2xl text-sm font-black transition-all group"
                onClick={() => setIsOpen(false)}
              >
                Join the waitlist
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
